import { Controller } from '@hotwired/stimulus'

import '../../entrypoints/modules/semantic-ui-dropdown'
import 'semantic-ui-transition/transition.min.js'
import 'semantic-ui-transition/transition.min.css'
import escapeHtml from '../../utils/sanitize'
import {
  trans,
  SETUP_TIMINGS,
  NO_FORTRESS_EVENTS_FOUND,
  SELECT_FORTRESS_EVENT,
  LOG_INTEGRATION_REQUIRED,
  LOG_TICKETS_SOLD_REQUIRED,
  LOG_TICKETS_SOLD_NUMBER_REQUIRED,
  LOG_FORTRESS_EVENT_REQUIRED,
  LOG_START
} from '../../translator'
import createFieldRule from '../../modules/validation/validation'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import moment from 'moment-timezone'

global.moment = moment

export default class extends Controller {
  static values = {
    entityType: String,
    logStartsAt: { type: String, default: null },
    logEndsAt: { type: String, default: null },
    logTimezone: { type: String, default: null },
    numberOfDays: { type: Number, default: 1 }
  }

  static targets = [
    'container'
  ]

  connect () {
    const logIntegrations = this.element.querySelectorAll('.logIntegration')
    this.setupLogIntegrations(logIntegrations)

    this.addToDropdownsHandler = this.addToDropdowns.bind(this)
    this.removeFromDropdownsHandler = this.removeFromDropdowns.bind(this)
    this.updateLogIntegrationsHandler = () => {
      this.updateLogIntegrations(logIntegrations)
    }
    this.updateNumberOfDaysHandler = (event) => {
      this.logStartsAtValue = event.detail.startsAt ? event.detail.startsAt : null
      this.logEndsAtValue = event.detail.endsAt ? event.detail.endsAt : null
      this.logTimezoneValue = event.detail.timezone
      this.updateNumberOfDays(event.detail.numberOfDays)
    }

    this.triggerConfiguredHandler = (event) => this.addTriggerToAnchorDropdowns(event.detail.triggerId, event.detail.triggerName, event.detail.day || '0')
    this.triggerClearedHandler = (event) => this.removeTriggerFromAnchorDropdowns(event.detail.triggerId, event.detail.day || '0')
    this.triggerTimesUpdatedHandler = () => this.refreshAllPollAnchorSubTexts()

    document.addEventListener('markers:marker:added', this.addToDropdownsHandler)
    document.addEventListener('markers:marker:removed', this.removeFromDropdownsHandler)
    document.addEventListener('markers:all-days-mode:updated', this.updateLogIntegrationsHandler)
    document.addEventListener('markers:date-range:updated', this.updateNumberOfDaysHandler)
    document.addEventListener('triggers:trigger:configured', this.triggerConfiguredHandler)
    document.addEventListener('triggers:trigger:cleared', this.triggerClearedHandler)
    document.addEventListener('triggers:times:updated', this.triggerTimesUpdatedHandler)
  }

  disconnect () {
    document.removeEventListener('markers:marker:added', this.addToDropdownsHandler)
    document.removeEventListener('markers:marker:removed', this.removeFromDropdownsHandler)
    document.removeEventListener('markers:all-days-mode:updated', this.updateLogIntegrationsHandler)
    document.removeEventListener('markers:date-range:updated', this.updateNumberOfDaysHandler)
    document.removeEventListener('triggers:trigger:configured', this.triggerConfiguredHandler)
    document.removeEventListener('triggers:trigger:cleared', this.triggerClearedHandler)
    document.removeEventListener('triggers:times:updated', this.triggerTimesUpdatedHandler)
  }

  formatMins (mins) {
    /* global accountIsUseAmPm */
    const h = Math.floor(mins / 60)
    const m = mins % 60
    if (typeof accountIsUseAmPm !== 'undefined' && accountIsUseAmPm) {
      const period = h >= 12 ? 'pm' : 'am'
      const h12 = h % 12 || 12
      return `${h12}:${String(m).padStart(2, '0')} ${period}`
    }
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  formatOffset (absMins) {
    if (absMins >= 60) {
      const h = Math.floor(absMins / 60)
      const m = absMins % 60
      return m > 0 ? `${h}h ${m}min` : `${h}h`
    }
    return `${absMins} min`
  }

  getMarkerSubText (markerId) {
    const markerRow = document.querySelector(`.marker-row[data-marker-id="${markerId}"]`)
    if (!markerRow) return ''

    if (markerRow.dataset.isAbsolute === 'true') {
      const mins = parseInt(markerRow.dataset.occursAtInMinutes)
      if (isNaN(mins)) return ''
      return this.formatMins(mins)
    }

    if (markerRow.dataset.isRelative === 'true') {
      const raw = parseInt(markerRow.dataset.relativeOffsetInMinutes)
      const absMins = isNaN(raw) ? 0 : Math.abs(raw)
      const direction = markerRow.dataset.relativeMarkerType === 'before' ? 'before' : 'after'
      const offsetText = this.formatOffset(absMins)
      const relId = markerRow.dataset.relativeMarkerId
      if (relId) {
        const relRow = document.querySelector(`.marker-row[data-marker-id="${relId}"]`)
        if (relRow) {
          const relSelect = relRow.querySelector('.log-event-select')
          const relName = relSelect ? $(relSelect).dropdown('get text') : ''
          if (relName) return `${offsetText} ${direction} ${relName}`
        }
      }
      return absMins > 0 ? `${offsetText} ${direction}` : ''
    }

    return ''
  }

  getTriggerSubText (triggerId) {
    const row = document.querySelector(`.trigger-row[data-trigger-id="${triggerId}"]`)
    if (!row) return ''

    const direction = row.dataset.triggerDirection
    if (!direction || direction === 'at') {
      const mins = parseInt(row.querySelector('.trigger-time-input')?.value)
      if (isNaN(mins)) return ''
      return this.formatMins(mins)
    }

    const days = parseInt(row.querySelector('.trigger-days-input')?.value) || 0
    const hours = parseInt(row.querySelector('.trigger-hours-input')?.value) || 0
    const minutes = parseInt(row.querySelector('.trigger-minutes-input')?.value) || 0
    const absMins = days * 1440 + hours * 60 + minutes
    const offsetText = this.formatOffset(absMins)

    const relInput = row.querySelector('.trigger-relative-input')
    const relTriggerId = relInput ? relInput.value : null
    if (relTriggerId) {
      const relRow = document.querySelector(`.trigger-row[data-trigger-id="${relTriggerId}"]`)
      if (relRow) {
        const relItem = relRow.querySelector(`.trigger-select .menu .item[data-value="${relTriggerId}"]`)
        const relName = relItem ? relItem.textContent.trim() : ''
        if (relName) return `${offsetText} ${direction} ${relName}`
      }
    }

    const relMarkerInput = row.querySelector('.trigger-relative-event-marker-input')
    const relEventMarkerId = relMarkerInput ? relMarkerInput.value : null
    if (relEventMarkerId) {
      const markerRow = document.querySelector(`.marker-row[data-log-marker-id="${relEventMarkerId}"]`)
      const markerName = markerRow ? markerRow.dataset.markerName : ''
      if (markerName) return `${offsetText} ${direction} ${markerName}`
    }

    return absMins > 0 ? `${offsetText} ${direction}` : ''
  }

  updatePollAnchorSelectedDisplay (anchorDropdown) {
    const selectedValue = $(anchorDropdown).dropdown('get value')
    const textEl = Array.from(anchorDropdown.children).find(el => el.classList.contains('text'))
    if (!textEl || !selectedValue) return

    const menu = anchorDropdown.querySelector('.menu')
    const selectedItem = menu ? menu.querySelector(`.item[data-value="${selectedValue}"]`) : null
    if (!selectedItem) return

    const anchorType = selectedItem.dataset.anchorType
    const itemName = selectedItem.querySelector('.poll-anchor-item-name')?.textContent?.trim() ||
      selectedItem.dataset.text || ''
    let subText = ''
    if (anchorType === 'marker') {
      subText = this.getMarkerSubText(selectedValue)
    } else if (anchorType === 'trigger') {
      subText = this.getTriggerSubText(selectedValue)
    }

    if (subText) {
      textEl.innerHTML = `${escapeHtml(itemName)}<span class="poll-anchor-selected-sub">${escapeHtml(subText)}</span>`
    } else {
      textEl.textContent = itemName
    }
    textEl.classList.remove('default')
  }

  sortTriggerItemsInMenu (menu) {
    if (!menu) return
    const triggerItems = Array.from(menu.querySelectorAll('.item[data-anchor-type="trigger"]'))
    if (triggerItems.length < 2) return

    const occursAt = (item) => {
      const row = document.querySelector(`.trigger-row[data-trigger-id="${item.dataset.value}"]`)
      const mins = row ? parseInt(row.querySelector('.trigger-time-input')?.value) : NaN
      return isNaN(mins) ? Number.POSITIVE_INFINITY : mins
    }

    // Trigger items always live at the end of the menu (after any marker section and
    // the trigger header), so re-appending them in time order preserves the layout.
    triggerItems
      .sort((a, b) => occursAt(a) - occursAt(b))
      .forEach(item => menu.appendChild(item))
  }

  refreshAllPollAnchorSubTexts () {
    this.element.querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
      const menu = anchorDropdown.querySelector('.menu')
      if (menu) this.refreshPollAnchorSubTexts(menu)
    })
  }

  refreshPollAnchorSubTexts (menu) {
    Array.from(menu.querySelectorAll('.item[data-anchor-type]')).forEach(item => {
      const anchorType = item.dataset.anchorType
      const value = item.dataset.value
      if (!value) return

      let subText = ''
      if (anchorType === 'marker') {
        subText = this.getMarkerSubText(value)
      } else if (anchorType === 'trigger') {
        subText = this.getTriggerSubText(value)
      }

      let subSpan = item.querySelector('.poll-anchor-item-sub')
      if (subText) {
        if (!subSpan) {
          subSpan = document.createElement('span')
          subSpan.classList.add('poll-anchor-item-sub')
          item.appendChild(subSpan)
        }
        subSpan.textContent = subText
      } else if (subSpan) {
        subSpan.remove()
      }
    })

    this.sortTriggerItemsInMenu(menu)

    const anchorDropdown = menu.closest('.poll-anchor-dropdown')
    if (anchorDropdown) this.updatePollAnchorSelectedDisplay(anchorDropdown)
  }

  updateTriggerOptionsForAnchor (anchorDropdown) {
    const menu = anchorDropdown.querySelector('.menu')
    if (!menu) return

    const pollSchedule = anchorDropdown.closest('.integrationPollSchedule')
    const pollDayIndex = pollSchedule ? parseInt(pollSchedule.dataset.index) || 0 : 0
    const isAllDays = this.numberOfDaysValue <= 1 || !!document.querySelector('#applyMarkersToAllDaysInput')?.checked

    document.querySelectorAll('.trigger-row[data-trigger-id]').forEach(row => {
      const triggerId = row.dataset.triggerId
      if (!triggerId) return

      const dayInput = row.querySelector('.trigger-day-input')
      const triggerDay = parseInt(dayInput ? dayInput.value : '0') || 0
      const shouldShow = isAllDays || triggerDay === pollDayIndex
      if (!shouldShow) return

      if (menu.querySelector(`.item[data-value="${triggerId}"][data-anchor-type="trigger"]`)) return

      const item = row.querySelector(`.trigger-select .menu .item[data-value="${triggerId}"]`)
      const triggerName = item ? item.textContent.trim() : ''
      if (!triggerName) return

      const newItem = document.createElement('div')
      newItem.classList.add('item')
      newItem.dataset.value = triggerId
      newItem.dataset.text = triggerName
      newItem.dataset.anchorType = 'trigger'
      newItem.dataset.triggerDay = String(triggerDay)
      newItem.innerHTML = `<span class="poll-anchor-item-name">${escapeHtml(triggerName)}</span>`
      menu.appendChild(newItem)

      const triggerHeader = menu.querySelector('.poll-anchor-trigger-header')
      if (triggerHeader) triggerHeader.classList.remove('d-none')

      const hasMarkers = !!menu.querySelector('.item[data-anchor-type="marker"]')
      const divider = menu.querySelector('.poll-anchor-divider')
      if (divider && hasMarkers) divider.classList.remove('d-none')
    })

    this.sortTriggerItemsInMenu(menu)
  }

  ensurePollAnchorPreselectedInMenu (anchorDropdown) {
    const initialValue = anchorDropdown.dataset.value
    const anchorType = anchorDropdown.dataset.initialAnchorType
    const initialText = anchorDropdown.dataset.initialText
    if (!initialValue || !anchorType || !initialText) return

    const menu = anchorDropdown.querySelector('.menu')
    if (!menu || menu.querySelector(`.item[data-value="${initialValue}"]`)) return

    const item = document.createElement('div')
    item.classList.add('item')
    item.dataset.value = initialValue
    item.dataset.text = initialText
    item.dataset.anchorType = anchorType
    item.innerHTML = `<span class="poll-anchor-item-name">${escapeHtml(initialText)}</span>`
    menu.appendChild(item)

    if (anchorType === 'trigger') {
      const triggerHeader = menu.querySelector('.poll-anchor-trigger-header')
      if (triggerHeader) triggerHeader.classList.remove('d-none')
    } else if (anchorType === 'marker') {
      const markerHeader = menu.querySelector('.poll-anchor-marker-header')
      if (markerHeader) markerHeader.classList.remove('d-none')
    }
  }

  setupPollAnchorDropdown (anchorDropdown) {
    this.updateTriggerOptionsForAnchor(anchorDropdown)
    this.ensurePollAnchorPreselectedInMenu(anchorDropdown)

    const preselectedValue = anchorDropdown.dataset.value || ''
    const container = anchorDropdown.closest('.poll-anchor-container')
    const controller = this

    $(anchorDropdown).dropdown({
      clearable: true,
      forceSelection: false,
      onShow: function () {
        const menu = this.querySelector('.menu')
        if (menu) {
          requestAnimationFrame(() => { menu.scrollTop = 0 })
          controller.refreshPollAnchorSubTexts(menu)
        }
      },
      onChange: (value, text, $item) => {
        const anchorType = $item ? $item.data('anchor-type') : null
        const markerInput = container?.querySelector('.poll-start-marker, .poll-end-marker')
        const triggerInput = container?.querySelector('.poll-start-trigger, .poll-end-trigger')
        if (anchorType === 'marker') {
          if (markerInput) markerInput.value = value
          if (triggerInput) triggerInput.value = ''
        } else if (anchorType === 'trigger') {
          if (triggerInput) triggerInput.value = value
          if (markerInput) markerInput.value = ''
        } else {
          if (markerInput) markerInput.value = ''
          if (triggerInput) triggerInput.value = ''
        }
        const scheduleRow = anchorDropdown.closest('.scheduleRow')
        if (scheduleRow) {
          scheduleRow.querySelectorAll('.pollTiming').forEach((pollTiming) => {
            pollTiming.classList.remove('error')
            pollTiming.querySelector('.prompt')?.remove()
          })
        }
        requestAnimationFrame(() => controller.updatePollAnchorSelectedDisplay(anchorDropdown))
      }
    })

    if (preselectedValue) {
      $(anchorDropdown).dropdown('set value', preselectedValue)
    }
    requestAnimationFrame(() => this.updatePollAnchorSelectedDisplay(anchorDropdown))
  }

  addTriggerToAnchorDropdowns (triggerId, triggerName, day = '0') {
    if (!triggerId || !triggerName) return
    const triggerDay = parseInt(day) || 0
    const isAllDays = this.numberOfDaysValue <= 1 || !!document.querySelector('#applyMarkersToAllDaysInput')?.checked
    this.element.querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
      const pollSchedule = anchorDropdown.closest('.integrationPollSchedule')
      const pollDayIndex = pollSchedule ? parseInt(pollSchedule.dataset.index) || 0 : 0
      const shouldShow = isAllDays || triggerDay === pollDayIndex
      if (!shouldShow) return

      const menu = anchorDropdown.querySelector('.menu')
      if (!menu || menu.querySelector(`.item[data-value="${triggerId}"][data-anchor-type="trigger"]`)) return

      const item = document.createElement('div')
      item.classList.add('item')
      item.dataset.value = triggerId
      item.dataset.text = triggerName
      item.dataset.anchorType = 'trigger'
      item.dataset.triggerDay = String(triggerDay)
      const subText = this.getTriggerSubText(triggerId)
      item.innerHTML = `<span class="poll-anchor-item-name">${escapeHtml(triggerName)}</span>${subText ? `<span class="poll-anchor-item-sub">${escapeHtml(subText)}</span>` : ''}`
      menu.appendChild(item)

      const triggerHeader = menu.querySelector('.poll-anchor-trigger-header')
      if (triggerHeader) triggerHeader.classList.remove('d-none')

      const hasMarkers = !!menu.querySelector('.item[data-anchor-type="marker"]')
      const divider = menu.querySelector('.poll-anchor-divider')
      if (divider && hasMarkers) divider.classList.remove('d-none')

      this.sortTriggerItemsInMenu(menu)

      $(anchorDropdown).dropdown('refresh')
    })
  }

  removeTriggerFromAnchorDropdowns (triggerId, day = '0') {
    const triggerDay = parseInt(day) || 0
    const isAllDays = this.numberOfDaysValue <= 1 || !!document.querySelector('#applyMarkersToAllDaysInput')?.checked
    this.element.querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
      const pollSchedule = anchorDropdown.closest('.integrationPollSchedule')
      const pollDayIndex = pollSchedule ? parseInt(pollSchedule.dataset.index) || 0 : 0
      if (!isAllDays && triggerDay !== pollDayIndex) return

      const menu = anchorDropdown.querySelector('.menu')
      if (!menu) return
      const item = menu.querySelector(`.item[data-value="${triggerId}"][data-anchor-type="trigger"]`)
      if (!item) return

      if ($(anchorDropdown).dropdown('get value') === triggerId) {
        $(anchorDropdown).dropdown('clear')
        const container = anchorDropdown.closest('.poll-anchor-container')
        const triggerInput = container?.querySelector('.poll-start-trigger, .poll-end-trigger')
        if (triggerInput) triggerInput.value = ''
      }

      item.remove()

      const hasTriggers = !!menu.querySelector('.item[data-anchor-type="trigger"]')
      const hasMarkers = !!menu.querySelector('.item[data-anchor-type="marker"]')
      const triggerHeader = menu.querySelector('.poll-anchor-trigger-header')
      const divider = menu.querySelector('.poll-anchor-divider')
      if (triggerHeader) triggerHeader.classList.toggle('d-none', !hasTriggers)
      if (divider) divider.classList.toggle('d-none', !hasMarkers || !hasTriggers)

      $(anchorDropdown).dropdown('refresh')
    })
  }

  setupLogIntegrations (logIntegrations) {
    logIntegrations.forEach((logIntegration) => {
      const integrationDropdown = logIntegration.querySelector('.integration')
      const newFormRule = {
        [integrationDropdown.id]: createFieldRule(integrationDropdown.id, 'empty', trans(LOG_INTEGRATION_REQUIRED, {}, 'logs'))
      }

      const form = $('#form')
      const currentFormRules = form.form('setting', 'fields')
      form.form('destroy')
      form.form({ fields: { ...currentFormRules, ...newFormRule }, inline: true })

      $(integrationDropdown).dropdown({
        forceSelection: false,
        onChange: (value, text, choice) => {
          if (integrationDropdown.dataset.readonly === 'true') {
            return
          }

          if (this.entityTypeValue === 'log' || this.entityTypeValue === 'logTemplate') {
            logIntegration.querySelectorAll('.red.pointing.prompt').forEach((errorPrompt) => {
              errorPrompt.remove()
            })

            logIntegration.querySelectorAll('.error').forEach((errorElement) => {
              errorElement.classList.remove('error')
            })

            logIntegration.querySelectorAll('.ticketsSold').forEach((ticketsSold) => {
              ticketsSold.value = null
            })

            const option = integrationDropdown.querySelector('option[value="' + value + '"]')
            const type = option ? option.dataset.type : null

            this.updateLogIntegrationByType(logIntegration, type)
          }
        }
      })

      if (integrationDropdown.dataset.readonly === 'true') {
        integrationDropdown.closest('.ui.dropdown').classList.add('disabled')
      }

      const value = $(integrationDropdown).dropdown('get value')
      const option = integrationDropdown.querySelector('option[value="' + value + '"]')
      const type = option ? option.dataset.type : null

      this.setupIntegrationPollSchedules(logIntegration, logIntegration.querySelectorAll('.integrationPollSchedule'))
      this.updateIntegrationPollSchedules(logIntegration.querySelectorAll('.integrationPollSchedule'))
      if (this.entityTypeValue === 'log' || this.entityTypeValue === 'logTemplate') {
        this.updateLogIntegrationByType(logIntegration, type)
      }
    })
  }

  updateLogIntegrationByType (logIntegration, type) {
    if (type === 'Fortress') {
      this.showTicketsSold(logIntegration)
      if (this.logStartsAtValue !== null && this.logEndsAtValue !== null) {
        logIntegration.querySelectorAll('.integrationPollSchedule').forEach((integrationPollSchedule) => {
          this.getFortressEvents(logIntegration, integrationPollSchedule)
        })
      }

      this.showFortressEvent(logIntegration)
    } else if (type === 'CEIA') {
      this.showTicketsSold(logIntegration)
      this.hideFortressEvent(logIntegration)
    } else {
      this.hideTicketsSold(logIntegration)
      this.hideFortressEvent(logIntegration)
    }
  }

  hideTicketsSold (logIntegration) {
    logIntegration.querySelectorAll('.ticketsSoldContainer').forEach((ticketsSoldContainer) => {
      ticketsSoldContainer.classList.add('d-none')
      ticketsSoldContainer.querySelector('.ticketsSold').disabled = true
    })
    logIntegration.querySelector('.ticketsSoldHeaderContainer').classList.add('d-none')
    logIntegration.querySelector('.ticketsSoldHeaderContainer').classList.remove('error')
  }

  showTicketsSold (logIntegration) {
    logIntegration.querySelectorAll('.ticketsSoldContainer').forEach((ticketsSoldContainer) => {
      ticketsSoldContainer.classList.remove('d-none')
      ticketsSoldContainer.querySelector('.ticketsSold').removeAttribute('disabled')
    })
    logIntegration.querySelector('.ticketsSoldHeaderContainer').classList.remove('d-none')
  }

  hideFortressEvent (logIntegration) {
    logIntegration.querySelectorAll('.fortressEventContainer').forEach((fortressEventContainer) => {
      fortressEventContainer.classList.add('d-none')
      fortressEventContainer.querySelector('input').disabled = true
    })
    logIntegration.querySelector('.fortressEventHeaderContainer')?.classList.add('d-none')
  }

  showFortressEvent (logIntegration) {
    logIntegration.querySelectorAll('.fortressEventContainer').forEach((fortressEventContainer) => {
      fortressEventContainer.classList.remove('d-none')
      fortressEventContainer.querySelector('input').removeAttribute('disabled')
    })

    logIntegration.querySelector('.fortressEventHeaderContainer')?.classList.remove('d-none')
  }

  setupIntegrationPollSchedules (logIntegration, integrationPollSchedules) {
    integrationPollSchedules.forEach((integrationPollSchedule) => {
      integrationPollSchedule.querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
        this.setupPollAnchorDropdown(anchorDropdown)
      })
    })
  }

  updateLogIntegrations (logIntegrations) {
    logIntegrations.forEach((logIntegration) => {
      this.updateIntegrationPollSchedules(logIntegration.querySelectorAll('.integrationPollSchedule'))
    })
  }

  updateIntegrationPollSchedules (integrationPollSchedules) {
    integrationPollSchedules.forEach((integrationPollSchedule) => {
      if (this.entityTypeValue === 'log') {
        this.updateDate(integrationPollSchedule)
        this.updateFormRules(integrationPollSchedule)
      }
      this.updatePollAnchorDropdownMarkers(integrationPollSchedule)
    })
  }

  updateDate (integrationPollSchedule) {
    const pollDate = integrationPollSchedule.querySelector('.pollDate')
    const numberOfDays = parseInt(pollDate.querySelector('input').value) ?? 0
    const logStartDate = moment(document.querySelector('#log-start-date').querySelector('input').value, accountDateFormat.toUpperCase())
    pollDate.querySelector('span').innerHTML = logStartDate.isValid()
      ? logStartDate.add(numberOfDays, 'days').format(accountDateFormat.toUpperCase())
      : trans(LOG_START, {}, 'logs')
  }

  updatePollAnchorDropdownMarkers (integrationPollSchedule) {
    integrationPollSchedule.querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
      const menu = anchorDropdown.querySelector('.menu')
      if (!menu) return

      Array.from(menu.querySelectorAll('.item[data-anchor-type="marker"]')).forEach(el => el.remove())
      Array.from(menu.querySelectorAll('.item[data-anchor-type="trigger"]')).forEach(el => el.remove())

      const isAllDays = this.numberOfDaysValue <= 1 || !!document.querySelector('#applyMarkersToAllDaysInput')?.checked
      let day
      if (isAllDays) {
        day = 0
      } else {
        day = integrationPollSchedule.dataset.index ?? 0
      }
      const pollDayIndex = parseInt(integrationPollSchedule.dataset.index) || 0

      const markerHeader = menu.querySelector('.poll-anchor-marker-header')
      const triggerHeader = menu.querySelector('.poll-anchor-trigger-header')
      const divider = menu.querySelector('.poll-anchor-divider')
      const insertBefore = divider || menu.querySelector('.poll-anchor-trigger-header')

      document.querySelectorAll(`.marker-group[data-day="${day}"] .log-event-select`).forEach((select) => {
        const value = $(select).dropdown('get value')
        const text = $(select).dropdown('get text')
        if (value !== '') {
          const subText = this.getMarkerSubText(value)
          const item = document.createElement('div')
          item.classList.add('item')
          item.dataset.value = value
          item.dataset.text = text
          item.dataset.anchorType = 'marker'
          item.innerHTML = `<span class="poll-anchor-item-name">${escapeHtml(text)}</span>${subText ? `<span class="poll-anchor-item-sub">${escapeHtml(subText)}</span>` : ''}`
          if (insertBefore) {
            menu.insertBefore(item, insertBefore)
          } else {
            menu.appendChild(item)
          }
        }
      })

      document.querySelectorAll('.trigger-row[data-trigger-id]').forEach(row => {
        const triggerId = row.dataset.triggerId
        if (!triggerId) return
        const dayInput = row.querySelector('.trigger-day-input')
        const triggerDay = parseInt(dayInput ? dayInput.value : '0') || 0
        const shouldShow = isAllDays || triggerDay === pollDayIndex
        if (!shouldShow) return
        const item = row.querySelector(`.trigger-select .menu .item[data-value="${triggerId}"]`)
        const triggerName = item ? item.textContent.trim() : ''
        if (!triggerName) return
        const subText = this.getTriggerSubText(triggerId)
        const newItem = document.createElement('div')
        newItem.classList.add('item')
        newItem.dataset.value = triggerId
        newItem.dataset.text = triggerName
        newItem.dataset.anchorType = 'trigger'
        newItem.dataset.triggerDay = String(triggerDay)
        newItem.innerHTML = `<span class="poll-anchor-item-name">${escapeHtml(triggerName)}</span>${subText ? `<span class="poll-anchor-item-sub">${escapeHtml(subText)}</span>` : ''}`
        menu.appendChild(newItem)
      })

      const hasMarkers = !!menu.querySelector('.item[data-anchor-type="marker"]')
      const hasTriggers = !!menu.querySelector('.item[data-anchor-type="trigger"]')
      if (markerHeader) markerHeader.classList.toggle('d-none', !hasMarkers)
      if (triggerHeader) triggerHeader.classList.toggle('d-none', !hasTriggers)
      if (divider) divider.classList.toggle('d-none', !hasMarkers || !hasTriggers)

      this.sortTriggerItemsInMenu(menu)

      const container = anchorDropdown.closest('.poll-anchor-container')
      const selectedValue = $(anchorDropdown).dropdown('get value')
      if (selectedValue && !menu.querySelector(`.item[data-value="${selectedValue}"]`)) {
        $(anchorDropdown).dropdown('clear')
        const markerInput = container?.querySelector('.poll-start-marker, .poll-end-marker')
        const triggerInput = container?.querySelector('.poll-start-trigger, .poll-end-trigger')
        if (markerInput) markerInput.value = ''
        if (triggerInput) triggerInput.value = ''
      }

      $(anchorDropdown).dropdown('refresh')
      requestAnimationFrame(() => this.updatePollAnchorSelectedDisplay(anchorDropdown))
    })
  }

  updateFormRules (integrationPollSchedule) {
    const ticketsSold = integrationPollSchedule.querySelector('.ticketsSold')
    const ticketsSoldFormRule = {
      [ticketsSold.id]: {
        identifier: ticketsSold.id,
        rules: [
          {
            type: 'empty',
            prompt: trans(LOG_TICKETS_SOLD_REQUIRED, {}, 'logs')
          },
          {
            type: 'regExp[/^[1-9]\\d*$/]',
            prompt: trans(LOG_TICKETS_SOLD_NUMBER_REQUIRED, {}, 'logs')
          }
        ]
      }
    }

    const fortressEventInput = integrationPollSchedule.querySelector('.fortressEvent input')
    const fortressEventFormRule = {
      [fortressEventInput.id]: createFieldRule(fortressEventInput.id, 'empty', trans(LOG_FORTRESS_EVENT_REQUIRED, {}, 'logs'))
    }

    const form = $('#form')
    const currentFormRules = form.form('setting', 'fields')
    form.form('destroy')
    form.form({ fields: { ...currentFormRules, ...ticketsSoldFormRule, ...fortressEventFormRule }, inline: true })
  }

  addToDropdowns (event) {
    const addMarkerToAnchorDropdown = (anchorDropdown, markerId, markerName) => {
      const menu = anchorDropdown.querySelector('.menu')
      if (!menu || menu.querySelector(`.item[data-value="${markerId}"][data-anchor-type="marker"]`)) return

      const markerHeader = menu.querySelector('.poll-anchor-marker-header')
      const divider = menu.querySelector('.poll-anchor-divider')
      const insertBefore = divider || menu.querySelector('.poll-anchor-trigger-header')

      const subText = this.getMarkerSubText(markerId)
      const item = document.createElement('div')
      item.classList.add('item')
      item.dataset.value = markerId
      item.dataset.text = markerName
      item.dataset.anchorType = 'marker'
      item.innerHTML = `<span class="poll-anchor-item-name">${escapeHtml(markerName)}</span>${subText ? `<span class="poll-anchor-item-sub">${escapeHtml(subText)}</span>` : ''}`
      if (insertBefore) {
        menu.insertBefore(item, insertBefore)
      } else {
        menu.appendChild(item)
      }

      const hasTriggers = !!menu.querySelector('.item[data-anchor-type="trigger"]')
      if (markerHeader) markerHeader.classList.remove('d-none')
      if (divider && hasTriggers) divider.classList.remove('d-none')

      $(anchorDropdown).dropdown('refresh')
    }

    if (event.detail.isApplyToAllDays || this.entityTypeValue === 'logTemplate') {
      this.element.querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
        addMarkerToAnchorDropdown(anchorDropdown, event.detail.markerId, event.detail.markerName)
      })
    } else {
      this.element.querySelectorAll('.day').forEach((pollDay) => {
        if (pollDay.value === event.detail.day) {
          pollDay.closest('.integrationPollSchedule').querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
            addMarkerToAnchorDropdown(anchorDropdown, event.detail.markerId, event.detail.markerName)
          })
        }
      })
    }
  }

  removeFromDropdowns (event) {
    const removeMarkerFromAnchorDropdown = (anchorDropdown, markerId) => {
      const menu = anchorDropdown.querySelector('.menu')
      if (!menu) return
      const item = menu.querySelector(`.item[data-value="${markerId}"][data-anchor-type="marker"]`)
      if (!item) return

      if ($(anchorDropdown).dropdown('get value') === markerId) {
        $(anchorDropdown).dropdown('clear')
        const container = anchorDropdown.closest('.poll-anchor-container')
        const markerInput = container?.querySelector('.poll-start-marker, .poll-end-marker')
        if (markerInput) markerInput.value = ''
      }

      item.remove()

      const hasMarkers = !!menu.querySelector('.item[data-anchor-type="marker"]')
      const hasTriggers = !!menu.querySelector('.item[data-anchor-type="trigger"]')
      const markerHeader = menu.querySelector('.poll-anchor-marker-header')
      const divider = menu.querySelector('.poll-anchor-divider')
      if (markerHeader) markerHeader.classList.toggle('d-none', !hasMarkers)
      if (divider) divider.classList.toggle('d-none', !hasMarkers || !hasTriggers)

      $(anchorDropdown).dropdown('refresh')
    }

    if (event.detail.isApplyToAllDays || this.entityTypeValue === 'logTemplate') {
      this.element.querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
        removeMarkerFromAnchorDropdown(anchorDropdown, event.detail.markerId)
      })
    } else {
      this.element.querySelectorAll('.day').forEach((pollDay) => {
        if (pollDay.value === event.detail.day) {
          pollDay.closest('.integrationPollSchedule').querySelectorAll('.poll-anchor-dropdown').forEach((anchorDropdown) => {
            removeMarkerFromAnchorDropdown(anchorDropdown, event.detail.markerId)
          })
        }
      })
    }
  }

  addLogIntegration () {
    const prototype = this.containerTarget.dataset.prototype
    const numberOfLogIntegrations = this.containerTarget.children.length
    const newForm = prototype.replace(/__name__/g, numberOfLogIntegrations)

    const newElement = document.createElement('div')
    newElement.classList.add('logIntegration')
    newElement.dataset.index = numberOfLogIntegrations
    newElement.innerHTML = newForm

    if (numberOfLogIntegrations > 0) {
      const hr = document.createElement('hr')
      newElement.prepend(hr)
    }

    for (let day = 0; day < this.numberOfDaysValue; day++) {
      this.addIntegrationPollSchedule(newElement, numberOfLogIntegrations, day)
    }

    this.containerTarget.appendChild(newElement)
    this.setupLogIntegrations([newElement])
  }

  addIntegrationPollSchedule (logIntegration, numberOfLogIntegrations, day) {
    const schedulePrototype = this.containerTarget.dataset.schedulePrototype
    const newScheduleForm = schedulePrototype
      .replace(/logIntegrations___name___/g, `logIntegrations_${numberOfLogIntegrations}_`)
      .replace(/logIntegrations]\[__name__/g, `logIntegrations][${numberOfLogIntegrations}`)
      .replace(/integrationPollSchedules___name___/g, `integrationPollSchedules_${day}_`)
      .replace(/integrationPollSchedules]\[__name__/g, `integrationPollSchedules][${day}`)
      .replace('data-index=""', `data-index="${day}"`)
      .replace('value="0"', `value="${day}"`)
      .replace(/__logIntegrationIndex__/g, numberOfLogIntegrations)
      .replace(/__integrationPollScheduleIndex__/g, day)

    logIntegration.querySelector('.integrationPollSchedules').insertAdjacentHTML('beforeend', newScheduleForm)
    if (this.entityTypeValue === 'log') {
      const integrationPollSchedules = logIntegration.querySelectorAll('.integrationPollSchedule')
      const lastIntegrationPollSchedule = integrationPollSchedules[integrationPollSchedules.length - 1]
      lastIntegrationPollSchedule.querySelector('.day').value = day
    }

    this.setupIntegrationPollSchedules(logIntegration, logIntegration.querySelectorAll('.integrationPollSchedule'))
  }

  updateNumberOfDays (numberOfDays) {
    this.numberOfDaysValue = numberOfDays
    this.element.querySelectorAll('.logIntegration').forEach((logIntegration, index) => {
      const integrationPollSchedules = logIntegration.querySelectorAll('.integrationPollSchedule')
      if (integrationPollSchedules.length < numberOfDays) {
        for (let day = integrationPollSchedules.length; day < numberOfDays; day++) {
          this.addIntegrationPollSchedule(logIntegration, index, day)
        }
      } else if (integrationPollSchedules.length > numberOfDays) {
        integrationPollSchedules.forEach((integrationPollSchedule, index) => {
          if (index >= numberOfDays) {
            integrationPollSchedule.remove()
          }
        })
      }

      this.updateIntegrationPollSchedules(logIntegration.querySelectorAll('.integrationPollSchedule'))

      const selectedOption = logIntegration.querySelector('.integration option[value="' + $(logIntegration.querySelector('.integration')).dropdown('get value') + '"]')
      const type = selectedOption ? selectedOption.dataset.type : null
      this.updateLogIntegrationByType(logIntegration, type)
    })

    this.element.querySelectorAll('.findFortressEventButton').forEach((findFortressEventButton) => {
      findFortressEventButton.classList.remove('disabled')
    })
  }

  removeLogIntegration (event) {
    const element = event.target.closest('.logIntegration')

    const form = $('#form')
    const currentFormRules = form.form('setting', 'fields')

    element.querySelectorAll('input, select, textarea').forEach((field) => {
      if (currentFormRules[field.id]) {
        delete currentFormRules[field.id]
      }
    })

    form.form('destroy')
    form.form({ fields: currentFormRules, inline: true })

    element.remove()
  }

  inLogTimezone (utcDateTime) {
    return moment.utc(utcDateTime).tz(this.logTimezoneValue).format('YYYY-MM-DDTHH:mm:ss')
  }

  getFortressEvents (logIntegration, integrationPollSchedule) {
    const dropdown = integrationPollSchedule.querySelector('.fortressEvent')
    $(dropdown).dropdown()

    const integrationId = $(logIntegration.querySelector('.integration')).dropdown('get value')
    if (!integrationId) {
      return
    }

    const eventSelected = !!integrationPollSchedule.querySelector('.fortressEvent .item.active')
    fetchWithHandlingLoginRedirect('/internal/api/integrations/' + integrationId + '/fortress-events?' +
      'startDate=' + this.inLogTimezone(this.logStartsAtValue) +
      '&endDate=' + this.inLogTimezone(this.logEndsAtValue) +
      '&logIntegrationFormIndex=' + logIntegration.dataset.index +
      '&integrationPollScheduleFormIndex=' + integrationPollSchedule.dataset.index
    )
      .then(response => {
        if (response.status === 200) {
          return response.json()
        }
        return Promise.reject(response)
      })
      .then(data => {
        const dropdownMenu = dropdown.querySelector('.menu')
        if (!eventSelected) {
          dropdown.querySelector('.text').classList.add('default')
        }

        if (data.length === 0 && !eventSelected) {
          dropdown.querySelector('.text').innerHTML = trans(NO_FORTRESS_EVENTS_FOUND, {}, 'logs')
          return
        } else {
          if (!eventSelected) {
            dropdown.querySelector('.text').innerHTML = trans(SELECT_FORTRESS_EVENT, {}, 'logs')
          }

          data.forEach(event => {
            const currentTitles = Array.from(dropdownMenu.children).map(item => item.dataset.title)
            if (!currentTitles.includes(event.title)) {
              const option = document.createElement('div')
              option.classList = 'item'
              option.textContent = event.title
              option.dataset.value = event.title
              option.dataset.title = event.title
              option.dataset.gameNumber = event.gameNumber
              option.dataset.eventTypeId = event.eventTypeId
              option.dataset.seasonId = event.seasonId
              dropdownMenu.appendChild(option)
            }
          })

          $(dropdown).dropdown('refresh')
        }

        const eventCount = Array.from(dropdownMenu.children).length
        if (eventCount && !eventSelected) {
          $(dropdown).dropdown('set selected', data[0].title)
        }
      })
  }
}
